import { inject } from '@angular/core';
import { Route, Router } from '@angular/router';
import { oryAuthRoutes } from '@ory-auth';
import { Observable, map, of, take, tap } from 'rxjs';

import { StubComponent } from '@aw/shared/components/stub';

import { JobTitle, Module } from '@aw/prypco/enums';
import { AuthRedirectGuard } from '@aw/prypco/guards/auth';
import { BlocksPage } from '@aw/prypco/modules/blocks/enum';
import { BlocksPortalPage } from '@aw/prypco/modules/blocks/modules/portal/enum';
import { DeviceService } from '@aw/prypco/services/device';
import { composeSeoRoute } from '@aw/prypco/services/seo';
import { AuthFacade } from '@aw/prypco/state/auth';

/**
 * Matcher for blocks mobile app to redirect to appropriate initial route
 */
const blocksMobileAppMatcher = (): Observable<boolean> => {
  const deviceService = inject(DeviceService);
  const authFacade = inject(AuthFacade);
  const router = inject(Router);

  if (deviceService.isApp) {
    return authFacade.authenticated$.pipe(take(1)).pipe(
      map((isAuthenticated) => {
        if (isAuthenticated) {
          return [BlocksPortalPage.Dashboard];
        }

        return [BlocksPage.AppLanding];
      }),
      tap((route) => void router.navigate(route)),
      map(() => false),
    );
  }

  return of(true);
};

const blocksAgentAppMatcher = (): Observable<boolean> => {
  const deviceService = inject(DeviceService);
  const router = inject(Router);
  const authFacade = inject(AuthFacade);

  if (deviceService.isApp && deviceService.appBusinessUnit === 'BACK_OFFICE') {
    return authFacade.jobTitle$.pipe(
      take(1),
      tap((jobTitle) => {
        // No specific redirection needed for blocks-only app
        return;
      }),
      map(() => true),
    );
  }

  return of(true);
};

const baseRoutes: Array<Route> = [
  composeSeoRoute(
    {
      path: '',
      canMatch: [blocksMobileAppMatcher],
      loadChildren: () =>
        import('@aw/prypco/modules/blocks/layout').then(
          (m) => m.BlocksLayoutModule,
        ),
    },
    {
      title: `Invest in Dubai's Real Estate (starting AED 2000) with our Crowdfunding Platform - PRYPCO Blocks`,
      description: `Invest in Dubai's real estate with our fractional ownership platform and earn passive income from anywhere in the world. Invest as little as AED 2000 with PRYPCO Blocks, regulated by DFSA and own a fraction of property in UAE.`,
    },
  ),
  {
    path: 'signup',
    pathMatch: 'full',
    redirectTo: '/auth/register',
  },
  {
    path: Module.Auth,
    children: oryAuthRoutes,
  },
  {
    path: Module.AuthRedirect,
    canActivate: [AuthRedirectGuard],
    component: StubComponent,
  },
  {
    path: '**',
    redirectTo: '',
  },
];

export const composeAppRoutes = (): Array<Route> => {
  return baseRoutes;
};
